import { useEffect, useState } from 'react';
import { AdvisoryForm } from '../../../features/Inquiry/AdvisoryForm';
import { ApiError, postJson } from '../../../lib/api/client';
import styles from './Modal.module.css';

/**
 * Inquiry modal — 4단계 플로우 (성공 시 3단계: intro → form → done)
 *   1) 'intro' — 체크 애니메이션 + "접수가 시작되었습니다" + 계속 진행하기
 *   2) 'form'  — 신청서 입력 폼 (제출 시 백엔드 호출)
 *   3) 'done'  — 접수 완료 안내
 *   4) 'error' — 제출 실패 시 안내 + 다시 시도
 */
export function Modal({ isOpen, onClose, email = '' }) {
  const [view, setView] = useState('intro');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Reset view each time the modal is opened
  useEffect(() => {
    if (isOpen) {
      setView('intro');
      setIsSubmitting(false);
      setErrorMessage('');
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (payload) => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await postJson('/apis/consult', payload);
      setView('done');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '네트워크 오류가 발생했습니다.';
      setErrorMessage(msg);
      setView('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={`${styles.box} ${view === 'form' ? styles.boxWide : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          className={styles.closeIcon}
          onClick={onClose}
          aria-label="닫기"
          type="button"
        >
          ×
        </button>

        {view === 'intro' && <IntroView onContinue={() => setView('form')} />}
        {view === 'form' && (
          <FormView email={email} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
        )}
        {view === 'done' && <DoneView onClose={onClose} />}
        {view === 'error' && (
          <ErrorView message={errorMessage} onRetry={() => setView('form')} />
        )}
      </div>
    </div>
  );
}

function AnimatedCheck() {
  return (
    <svg
      className={styles.checkSvg}
      viewBox="0 0 52 52"
      aria-hidden="true"
      role="img"
    >
      <circle
        className={styles.checkCircle}
        cx="26"
        cy="26"
        r="24"
        fill="none"
      />
      <path
        className={styles.checkMark}
        fill="none"
        d="M14.5 27.2l7 7 16-16"
      />
    </svg>
  );
}

function IntroView({ onContinue }) {
  return (
    <>
      <AnimatedCheck />
      <div className={styles.title}>접수가 시작되었습니다</div>
      <div className={styles.body}>
        담당 변호사에게 전달될 신청서를 작성해 주세요.
        <br />
        1영업일 이내에 연락드립니다.
      </div>
      <button
        type="button"
        className={styles.primaryCta}
        onClick={onContinue}
        autoFocus
      >
        계속 진행하기
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 8h10M9 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </>
  );
}

function FormView({ email, onSubmit, isSubmitting }) {
  return (
    <>
      <div className={styles.formHead}>
        <div className={styles.formTitle}>상담 신청서 작성</div>
      </div>
      <div className={styles.formBody}>
        <AdvisoryForm onSubmit={onSubmit} defaultEmail={email} isSubmitting={isSubmitting} />
      </div>
    </>
  );
}

function DoneView({ onClose }) {
  return (
    <>
      <AnimatedCheck />
      <div className={styles.title}>접수가 완료되었습니다</div>
      <div className={styles.body}>
        담당 변호사가 1영업일 이내에 연락드리겠습니다.
        <br />
        법무법인 오킴스 의료팀 medilawyer를 이용해 주셔서 감사합니다.
      </div>
      <button className={styles.close} onClick={onClose}>
        확인
      </button>
    </>
  );
}

function ErrorView({ message, onRetry }) {
  return (
    <>
      <div className={styles.title}>제출에 실패했습니다</div>
      <div className={styles.body}>
        {message}
        <br />
        잠시 후 다시 시도해 주세요.
      </div>
      <button type="button" className={styles.primaryCta} onClick={onRetry}>
        다시 시도
      </button>
    </>
  );
}
